import { useState, useEffect } from 'react';
import { SUBJECTS, TOPICS, SUBJECT_COLORS } from '../data';
import supabase from '../lib/supabase';

function readinessColor(pct) {
  if (pct >= 75) return '#4caf50';
  if (pct >= 50) return '#ff9800';
  return '#e53935';
}

function Progress({ getTopicAccuracy, getTotalQuestions, getReadinessScore, t }) {
  const total = getTotalQuestions();
  const readiness = getReadinessScore();
  const locked = total < 10;

  const [examHistory, setExamHistory] = useState([]);
  const [inProgressExam, setInProgressExam] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) return;
      const userId = session.user.id;

      // Completed exams
      const { data: completed } = await supabase
        .from('mock_exam_scores')
        .select('score, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);
      if (completed) setExamHistory(completed);

      // In-progress exam session
      const { data: session_data } = await supabase
        .from('exam_sessions')
        .select('scores, phase, updated_at')
        .eq('user_id', userId)
        .single();
      if (session_data && session_data.phase && session_data.phase !== 'intro') {
        const scores = session_data.scores || {};
        const totalCorrect = Object.values(scores).reduce((s, v) => s + (v.correct || 0), 0);
        const totalAnswered = Object.values(scores).reduce((s, v) => s + (v.total || 0), 0);
        if (totalAnswered > 0) {
          setInProgressExam({
            score: Math.round((totalCorrect / totalAnswered) * 100),
            answered: totalAnswered,
            updated_at: session_data.updated_at,
          });
        }
      }
    });
  }, []);

  return (
    <div className="progress-screen">

      {/* ── Study Progress ── */}
      <h2 className="progress-section-heading">{t('studyProgress')}</h2>

      <div className="readiness-card">
        <div className="readiness-header">
          <span className="readiness-label">{t('gedReadiness')}</span>
          {locked && <span className="readiness-lock">{t('unlocksAfter', total)}</span>}
        </div>
        <div className="progress-bar-bg">
          <div
            className="progress-bar-fill"
            style={{
              width: locked ? '0%' : `${readiness}%`,
              background: locked ? '#ccc' : readinessColor(readiness),
            }}
          />
        </div>
        {!locked && <p className="readiness-score">{t('accuracyAll', readiness)}</p>}
      </div>

      <div className="subjects-grid">
        {SUBJECTS.map(subject => {
          const color = SUBJECT_COLORS[subject];
          return (
            <div key={subject} className="subject-section">
              <h3 className="subject-section-title" style={{ color }}>{subject}</h3>
              {TOPICS[subject].map(topic => {
                const acc = getTopicAccuracy(topic);
                return (
                  <div key={topic} className="progress-row">
                    <span className="progress-label">{topic}</span>
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{ width: acc !== null ? `${acc}%` : '0%', background: color }}
                      />
                    </div>
                    <span className="progress-pct">{acc !== null ? `${acc}%` : '—'}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {total === 0 && (
        <p className="empty-progress">{t('completeQuiz')}</p>
      )}

      {/* ── Mock Exam History ── */}
      <h2 className="progress-section-heading" style={{ marginTop: 40 }}>{t('mockExamHistory')}</h2>

      {inProgressExam && (
        <div className="exam-history-row exam-history-row--inprogress">
          <span className="exam-history-date">{t('inProgress')}</span>
          <div className="progress-bar-bg" style={{ flex: 1, margin: '0 12px' }}>
            <div
              className="progress-bar-fill exam-progress-bar--inprogress"
              style={{ width: `${inProgressExam.score}%` }}
            />
          </div>
          <span className="exam-history-score exam-history-score--inprogress">
            {inProgressExam.score}% <span className="exam-inprogress-qs">({inProgressExam.answered} Qs)</span>
          </span>
        </div>
      )}

      {examHistory.length === 0 && !inProgressExam ? (
        <p className="empty-progress">{t('noExams')}</p>
      ) : (
        <div className="exam-history-list">
          {examHistory.map((entry, i) => {
            const date = new Date(entry.created_at).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            });
            const color = readinessColor(entry.score);
            return (
              <div key={i} className="exam-history-row">
                <span className="exam-history-date">{date}</span>
                <div className="progress-bar-bg" style={{ flex: 1, margin: '0 12px' }}>
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${entry.score}%`, background: color }}
                  />
                </div>
                <span className="exam-history-score" style={{ color }}>{entry.score}%</span>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

export default Progress;
